import {
 createContext,
 Dispatch,
 ReactNode,
 SetStateAction,
 useCallback,
 useContext,
 useMemo,
 useState,
} from 'react';

import { INITIAL_MODAL_STATE } from './constants';
import {
 FilterMenuState,
 LimitModalState,
 TableModalState,
 TranslationModalState,
} from './types';

interface XTableContextType {
 modalState: TableModalState;
 setModalState: Dispatch<SetStateAction<TableModalState>>;
 createModalOpen: boolean;
 setCreateModalOpen: Dispatch<SetStateAction<boolean>>;
 colModalOpen: boolean;
 setColModalOpen: Dispatch<SetStateAction<boolean>>;
 filterMenuState: FilterMenuState | null;
 setFilterMenuState: Dispatch<
  SetStateAction<FilterMenuState | null>
 >;
 limitModalState: LimitModalState | null;
 setLimitModalState: Dispatch<
  SetStateAction<LimitModalState | null>
 >;
 translationModalState: TranslationModalState | null;
 setTranslationModalState: Dispatch<
  SetStateAction<TranslationModalState | null>
 >;
 currentId: number | string | undefined;
 setCurrentId: Dispatch<
  SetStateAction<number | string | undefined>
 >;
 filterSidebarOpen: boolean;
 setFilterSidebarOpen: Dispatch<SetStateAction<boolean>>;
}

const XTableContext =
 createContext<XTableContextType | null>(null);

export const useXTableContext = (): XTableContextType => {
 const context = useContext(XTableContext);
 if (!context)
  throw new Error(
   'useXTableContext must be used within XTableProvider',
  );
 return context;
};

export const XTableProvider = ({
 children,
}: {
 children: ReactNode;
}) => {
 const [modalState, setModalState] =
  useState<TableModalState>(INITIAL_MODAL_STATE);
 const [createModalOpen, setCreateModalOpen] = useState(false);
 const [colModalOpen, setColModalOpen] = useState(false);
 const [filterMenuState, setFilterMenuState] =
  useState<FilterMenuState | null>(null);
 const [limitModalState, setLimitModalState] =
  useState<LimitModalState | null>(null);
 const [translationModalState, setTranslationModalState] =
  useState<TranslationModalState | null>(null);
 const [currentIdInternal, setCurrentIdInternal] = useState<
  number | string | undefined
 >();
 const [
  filterSidebarOpenInternal,
  setFilterSidebarOpenInternal,
 ] = useState(false);

 const setCurrentId: Dispatch<
  SetStateAction<number | string | undefined>
 > = useCallback((value) => {
  setCurrentIdInternal((prev) => {
   const next =
    typeof value === 'function' ? value(prev) : value;
   if (next !== undefined) {
    setFilterSidebarOpenInternal(false);
   }
   return next;
  });
 }, []);

 const setFilterSidebarOpen: Dispatch<
  SetStateAction<boolean>
 > = useCallback((value) => {
  setFilterSidebarOpenInternal((prev) => {
   const next =
    typeof value === 'function' ? value(prev) : value;
   if (next) {
    setCurrentIdInternal(undefined);
   }
   return next;
  });
 }, []);

 const value = useMemo<XTableContextType>(
  () => ({
   modalState,
   setModalState,
   createModalOpen,
   setCreateModalOpen,
   colModalOpen,
   setColModalOpen,
   filterMenuState,
   setFilterMenuState,
   limitModalState,
   setLimitModalState,
   translationModalState,
   setTranslationModalState,
   currentId: currentIdInternal,
   setCurrentId,
   filterSidebarOpen: filterSidebarOpenInternal,
   setFilterSidebarOpen,
  }),
  [
   modalState,
   setModalState,
   createModalOpen,
   setCreateModalOpen,
   colModalOpen,
   setColModalOpen,
   filterMenuState,
   setFilterMenuState,
   limitModalState,
   setLimitModalState,
   translationModalState,
   setTranslationModalState,
   currentIdInternal,
   setCurrentId,
   filterSidebarOpenInternal,
   setFilterSidebarOpen,
  ],
 );

 return (
  <XTableContext.Provider value={value}>
   {children}
  </XTableContext.Provider>
 );
};
